import type { LanguageValue } from 'src/locales';
import type { IconButtonProps } from '@mui/material/IconButton';

import { m } from 'framer-motion';
import { useCallback } from 'react';

import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';

import dayjs from 'dayjs';
import { useTranslate } from 'src/locales';

import { varHover } from 'src/components/animate';
import { FlagIcon, Iconify } from 'src/components/iconify';
import { usePopover, CustomPopover } from 'src/components/custom-popover';
import { Button } from '@mui/material';

import Storage from 'src/utils/Storage'; // Assuming this handles local storage

// ----------------------------------------------------------------------

export type LanguagePopoverProps = IconButtonProps & {
  data?: {
    value: string;
    label: string;
    countryCode: string;
  }[];
  showLabel?: boolean;
};

export function LanguagePopover({ data = [], sx, showLabel, ...other }: LanguagePopoverProps) {
  const popover = usePopover();

  const { onChangeLang, currentLang } = useTranslate();

  const handleChangeLang = useCallback(
    (newLang: LanguageValue) => {
      // Update language in useTranslate
      onChangeLang(newLang);

      // Save language in storage
      // Storage.removeItem('i18nextLng');
      // Storage.setItem('i18nextLng', newLang);

      // Close the popover
      popover.onClose();
    },
    [onChangeLang, popover]
  );

  return (
    <>
      {showLabel ? (
        <Button startIcon={<FlagIcon code={currentLang.countryCode} />} onClick={popover.onOpen}>
          {currentLang.label}
        </Button>
      ) : (
        <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          onClick={popover.onOpen}
          sx={{
            width: 40,
            height: 40,
            ...(popover.open && {
              bgcolor: 'action.selected',
            }),
          }}
          {...other}
        >
          <FlagIcon code={currentLang.countryCode} />
        </IconButton>
      )}

      <CustomPopover open={popover.open} anchorEl={popover.anchorEl} onClose={popover.onClose}>
        <MenuList sx={{ width: 160, minHeight: 72 }}>
          {data?.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === currentLang.value}
              onClick={() => handleChangeLang(option.value as LanguageValue)}
            >
              <FlagIcon code={option.countryCode} />
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </CustomPopover>
    </>
  );
}
